import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  tap,
  Subject,
  of,
  switchMap,
  catchError,
  BehaviorSubject,
  takeUntil,
  debounceTime,
  map,
} from "rxjs";
import PropTypes from "prop-types";
import Pagination from "../../../atoms/Pagination/PaginationSimple";
import Autocomplete from "../../../atoms/Autocomplete";
import rxErrorHandler from "../../../../common/helpers/rxErrorHandler";
import apiClient from "../../../../services/apiClient";
import Tag from "../../models/Tag";
import TagFilterPill from "../TagPill/TagFilterPill";
import { pluralize } from "../../../../common/helpers/helpers";
import { SEPERATOR, TAG_TITLES } from "../../constants/tagConstants";
import Toggle from "../../../atoms/Toggle";
import TimeLine from "../TimeLine";
import SourceEntity from "../../models/SourceEntity";
import {
  SORT_OPTIONS_REL,
  SORT_OPTIONS_NO_REL,
  SORT_OPTION_DICT,
} from "../../constants/sortOptions";

function parseTag(x) {
  const { key, doc_count: count } = x;
  const parts = key.split(SEPERATOR);
  const tagType = parts[0];
  const normalized = parts.slice(1).join(SEPERATOR);
  return new Tag({ tagType, key, normalized, text: normalized, count });
}

function filterTags(tagOptions = [], str, excluded, limit = 20) {
  const exSet = new Set(excluded.map((x) => x.key));
  const words = str.split(" ").filter(Boolean);
  const r = words.map((x) => `(\\W|^)${x}`).join("|");
  const regex = new RegExp(r, "gi");
  const wordLimit = words.length;

  return tagOptions
    .map((x) => ({ tag: x, match: x.normalized && x.normalized.match(regex) }))
    .filter((x) => x.match && x.match.length >= wordLimit)
    .filter((x) => x.tag.key && !exSet.has(x.tag.key))
    .slice(0, limit)
    .map((x) => x.tag);
}

function PublicationFilters({
  showTimeline,
  setShowTimeline,
  selectedTags,
  setSelectedTags,
  timelineTag,
  setTimelineTag,
  type,
  typeId,
  totalResults,
  orderBy,
  setOrderBy,
  pageSize,
  setPageSize,
  page,
  setPage,
  filterMode,
  setFilterMode,
  sourceEntity,
}) {
  const inputRef = useRef(null);
  const pageSizeOptions = useRef([10, 20, 50]).current;
  const value$ = useRef(new BehaviorSubject("")).current;
  const [tagOptions, setTagOptions] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [filteredTags, setFilteredTags] = useState([]);
  const start = (page - 1) * pageSize;
  const end = Math.min(page * pageSize, totalResults);
  const sortOptions = sourceEntity?.id ? SORT_OPTIONS_REL : SORT_OPTIONS_NO_REL;
  const { val: firstSort } = sortOptions[0];
  const sortSelected = SORT_OPTION_DICT[orderBy] || SORT_OPTION_DICT[firstSort];

  const pageSizeOptionsFixed = useMemo(() => {
    return pageSizeOptions.map((x) => ({ val: x, label: `${x}` }));
  }, [pageSizeOptions]);

  const selectedPaginationSize = useMemo(() => {
    return { val: pageSize, label: String(pageSize) };
  }, [pageSize]);

  useEffect(() => {
    setFilteredTags([]);
    setTagOptions([]);
  }, [typeId, type]);

  useEffect(() => {
    if (!isActive) return () => {};
    const size = 10000;
    const { getTagAggregations } = apiClient;
    const destroy$ = new Subject();
    const field = "taggedWithTags";
    const tagFilterMode = filterMode;
    const isUnion = tagFilterMode === "union";
    const tagsFiltered = isUnion ? [] : selectedTags;

    const tags = tagsFiltered.concat([timelineTag]).filter(Boolean);
    const yearTags = tags.filter((x) => x.year).map((x) => x.key);
    const nonYearTags = tags.filter((x) => !x.year).map((x) => x.typeKey);
    const tagFilters = nonYearTags.join("~");
    const tagYearFilters = yearTags.join("~");
    const filters = { tagFilterMode, tagFilters, tagYearFilters };
    const params = { size, field, ...filters };

    of(null)
      .pipe(
        switchMap(() => getTagAggregations(type, typeId, params)),
        map((x) => x.results),
        map((x) => x.map(parseTag)),
        tap((x) => setTagOptions(x)),
        catchError(rxErrorHandler(() => {}, null)),
        takeUntil(destroy$)
      )
      .subscribe();

    return () => destroy$.next();
  }, [isActive, typeId, type, selectedTags, timelineTag, filterMode]);

  useEffect(() => {
    const destroy$ = new Subject();

    value$
      .pipe(
        debounceTime(200),
        map((str) => filterTags(tagOptions, str, selectedTags, 20)),
        takeUntil(destroy$),
        catchError(rxErrorHandler(() => {}, [])),
        tap((x) => setFilteredTags(x))
      )
      .subscribe();

    return () => destroy$.next(true);
  }, [tagOptions, selectedTags]);

  const onSelect = (x) => {
    setSelectedTags([...selectedTags, x]);
  };

  const handleRemove = (x) => {
    setSelectedTags(selectedTags.filter((y) => y.key !== x.key));
  };

  const titleCl = "tw-text-xs tw-text-blueGray-400 tw-min-w-0 tw-truncate";

  return (
    <div className="tw-text-sm">
      <div className="tw-flex tw-items-center tw-gap-x-4 tw-mb-3">
        <div className="tw-flex-grow" />
        <div className={titleCl}>Show Tags Timeline</div>
        <div className="tw-text-blueGray-700">
          <Toggle
            isChecked={showTimeline}
            sizeClass="tw-w-8 tw-my-1"
            onChange={(x) => setShowTimeline(x)}
          />
        </div>
      </div>

      {showTimeline ? (
        <TimeLine
          showTimeline={true}
          selectedItem={timelineTag}
          setSelectedTags={setTimelineTag}
          type={type}
          typeId={typeId}
        />
      ) : null}

      <div className="tw-flex tw-w-full tw-gap-x-5 tw-gap-y-4 tw-pb-2 tw-flex-wrap-reverse">
        <div className="tw-flex tw-gap-x-5 tw-min-w-99">
          <div>
            <div className={titleCl} data-testid="page-size-test">
              Search for Tags
            </div>

            <div className="tw-relative" onClick={() => setIsActive(true)}>
              <Autocomplete
                value={{}}
                optionKey="key"
                className="tw-border tw-top-2 tw-border-blueGray-300 tw-h-8 tw-flex tw-items-center tw-px-2"
                options={filteredTags}
                onSelect={onSelect}
                OptionsWrapper={({ children }) => (
                  <div className="tw-bg-white tw-max-h-40 tw-overflow-auto tw-rounded tw-mt-2 tw-border tw-border-blueGray-200 tw-shadow-strong">
                    {children}
                  </div>
                )}
                OptionContent={({ option, isHighlighted }) => {
                  return (
                    <div
                      className={`tw-flex prevent-tooltip-close tw-py-1 tw-cursor-pointer tw-px-2 ${
                        isHighlighted
                          ? "tw-bg-blueGray-100 tw-text-blueGray-800"
                          : ""
                      }`}
                    >
                      <div className="tw-flex-grow tw-min-w-66">
                        {option.text}
                      </div>
                      <div className="tw-mx-2" />
                      <div className="tw-flex-shrink-0">
                        {TAG_TITLES[option.tagType]}
                      </div>
                      <div className="tw-border-l tw-border-blueGray-300 tw-mx-2" />
                      <div className="tw-w-24 tw-flex-shrink-0">
                        <b>{option.count}</b>{" "}
                        {pluralize(option.count, "Article", "Articles")}
                      </div>
                    </div>
                  );
                }}
              >
                <input
                  type="text"
                  ref={inputRef}
                  className="tw-border tw-border-blueGray-300 tw-flex tw-items-center tw-px-2 tw-py-1"
                  placeholder="type keyword..."
                  onChange={(e) => value$.next(e.target.value)}
                />
              </Autocomplete>
            </div>
          </div>

          {selectedTags.length > 1 ? (
            <div>
              <div className={titleCl} data-testid="page-size-test">
                Filter Mode
              </div>
              <div className="tw-text-blueGray-700">
                <Autocomplete
                  value={{ label: filterMode }}
                  optionKey="label"
                  className="tw-border tw-border-blueGray-300 tw-flex tw-items-center tw-px-2"
                  options={["union", "intersection"].map((x) => ({ label: x }))}
                  onSelect={(x) => setFilterMode(x.label)}
                />
              </div>
            </div>
          ) : null}
        </div>

        <div className="tw-flex-grow" />

        <div className="tw-flex tw-gap-x-5 tw-min-w-99">
          <div>
            <div className={titleCl}>
              Showing {start} - {end} of {totalResults}
            </div>
            <div className="tw-text-blueGray-700 tw-pt-1">
              <Pagination
                totalResults={totalResults}
                currentPage={page}
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                onPageChange={setPage}
              />
            </div>
          </div>

          {pageSizeOptions && pageSizeOptions.length > 1 ? (
            <div>
              <div className={titleCl} data-testid="page-size-test">
                Page size
              </div>

              <div className="tw-text-blueGray-700">
                <Autocomplete
                  value={selectedPaginationSize}
                  optionKey="val"
                  className="tw-border tw-border-blueGray-300 tw-flex tw-items-center tw-px-2"
                  options={pageSizeOptionsFixed}
                  onSelect={(x) => setPageSize(x.val)}
                />
              </div>
            </div>
          ) : null}

          <div>
            <div className={titleCl}>Order by</div>
            <div className="tw-text-blueGray-700">
              <Autocomplete
                value={sortSelected}
                optionKey="val"
                className="tw-border tw-border-blueGray-300 tw-flex tw-items-center tw-px-2"
                options={sortOptions}
                onSelect={(x) => setOrderBy(x.val)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="tw-flex tw-gap-x-1 tw-flex-wrap tw-text-xs">
        {selectedTags.map((x) => (
          <TagFilterPill key={x.key} tag={x} onRemove={handleRemove} />
        ))}
      </div>
    </div>
  );
}

PublicationFilters.propTypes = {
  setShowTimeline: PropTypes.func.isRequired,
  showTimeline: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  typeId: PropTypes.string.isRequired,
  totalResults: PropTypes.number.isRequired,
  selectedTags: PropTypes.arrayOf(PropTypes.instanceOf(Tag)).isRequired,
  timelineTag: PropTypes.instanceOf(Tag),
  setSelectedTags: PropTypes.func.isRequired,
  setTimelineTag: PropTypes.func.isRequired,
  setOrderBy: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  setFilterMode: PropTypes.func.isRequired,
  displayOptions: PropTypes.shape({}),
  filterMode: PropTypes.string,
  orderBy: PropTypes.string,
  pageSize: PropTypes.number,
  sourceEntity: PropTypes.instanceOf(SourceEntity),
};

PublicationFilters.defaultProps = {
  timelineTag: null,
  filterMode: null,
  orderBy: null,
  pageSize: 10,
  displayOptions: {},
  sourceEntity: null,
};

export default PublicationFilters;
